import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Seo, faqLd, breadcrumbLd } from "@/components/site/Seo";
import { PageHero, CtaBand, FaqList, ContactAside } from "@/components/site/Common";
import { RichText, Sections } from "@/components/site/RichText";
import { Loading } from "@/pages/Pages";
import NotFound from "@/pages/NotFound";
import { SITE_URL } from "@/lib/api";

function Block({ title, children, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="mb-4 text-xl font-bold text-zinc-950 sm:text-2xl">{title}</h2>
      {children}
    </section>
  );
}

function CheckList({ items = [] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {items.map((t) => (
        <li key={t} className="flex gap-3 rounded-md border border-zinc-200 bg-white p-4 text-sm text-zinc-700">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-[color:var(--red)]" /> {t}
        </li>
      ))}
    </ul>
  );
}

function ServiceBody({ d }) {
  return (
    <div className="space-y-14">
      <RichText text={d.intro} className="text-base" />
      <Block title="Hangi ihtiyaçları karşılar?" testid="service-needs"><CheckList items={d.needs} /></Block>
      <Block title="Uygulama ve planlama yaklaşımımız" testid="service-approach"><RichText text={d.approach} /></Block>
      <Block title="Risk değerlendirmesinin önemi" testid="service-risk">
        <div className="border-l-4 border-[color:var(--red)] bg-zinc-50 p-5"><RichText text={d.risk} /></div>
      </Block>
      <Block title="Planlama aşamaları" testid="service-steps">
        <ol className="space-y-3">
          {(d.steps || []).map((s, i) => (
            <li key={s} className="flex items-center gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[color:var(--ink)] font-display text-sm font-bold text-white">{i + 1}</span>
              <span className="text-sm font-medium text-zinc-800 sm:text-base">{s}</span>
            </li>
          ))}
        </ol>
      </Block>
      <Block title="Faydalar" testid="service-benefits"><CheckList items={d.benefits} /></Block>
    </div>
  );
}

function OtherLinks({ current }) {
  const { services } = useSite();
  const others = useMemo(() => services.filter((s) => s.slug !== current), [services, current]);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-6">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-zinc-900">Diğer hizmetler</h3>
      <ul className="space-y-1">
        {others.map((s) => (
          <li key={s.slug}>
            <Link to={`/hizmetler/${s.slug}`} className="flex items-center justify-between rounded px-2 py-2 text-sm text-zinc-700 transition-colors hover:bg-zinc-50 hover:text-[color:var(--red)]">
              {s.title} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const { services } = useSite();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    setState({ loading: true });
    api.get(`/public/services/${slug}`)
      .then((r) => setState({ type: "service", d: r.data }))
      .catch(() => api.get(`/public/sectors/${slug}`)
        .then((r) => setState({ type: "sector", d: r.data }))
        .catch(() => setState({ notFound: true })));
  }, [slug]);

  if (state.loading) return <Loading />;
  if (state.notFound) return <NotFound />;
  const { d, type } = state;
  const path = `/hizmetler/${d.slug}`;
  const parent = type === "service" ? { name: "Hizmetlerimiz", path: "/hizmetlerimiz" } : { name: "Sektörler", path: "/sektorler" };
  const related = type === "sector" ? services.find((s) => s.slug === d.service_slug) : null;
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service", name: d.title, description: d.short, serviceType: d.title,
    provider: { "@id": `${SITE_URL}/#business` }, areaServed: ["Kuşadası", "Aydın", "Ege Bölgesi"], url: `${SITE_URL}${path}`,
  };

  return (
    <>
      <Seo title={d.seo_title} description={d.seo_description} path={path} image={d.image}
        jsonLd={[serviceLd, faqLd(d.faqs), breadcrumbLd([{ name: "Ana Sayfa", path: "/" }, parent, { name: d.title, path }])].filter(Boolean)} />
      <PageHero title={d.title} subtitle={d.short} image={d.image} crumbs={[parent, { name: d.title }]} eyebrow={type === "service" ? "Hizmet" : "Sektör / Bölge"} />
      <div className="bg-zinc-50">
        <div className="container-x grid gap-12 py-14 sm:py-20 lg:grid-cols-[1fr_340px]">
          <article data-testid={`detail-${type}`}>
            {type === "service" ? <ServiceBody d={d} /> : <Sections sections={d.sections} />}
            {related && (
              <Link to={`/hizmetler/${related.slug}`} className="mt-10 flex items-center justify-between rounded-lg border border-zinc-200 bg-white p-5 transition-colors hover:border-[color:var(--red)]" data-testid="related-service-link">
                <span><span className="block text-xs font-bold uppercase tracking-widest text-zinc-500">İlgili hizmet</span><span className="font-bold text-zinc-900">{related.title}</span></span>
                <ArrowRight className="h-5 w-5 text-[color:var(--red)]" />
              </Link>
            )}
            {d.faqs?.length > 0 && (
              <section className="mt-14">
                <h2 className="mb-4 text-xl font-bold text-zinc-950 sm:text-2xl">Sıkça sorulan sorular</h2>
                <FaqList faqs={d.faqs} testid="detail-faq" />
              </section>
            )}
          </article>
          <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <ContactAside />
            <OtherLinks current={d.slug} />
          </div>
        </div>
      </div>
      <CtaBand />
    </>
  );
}
